"use client";

import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Languages, Mic, PhoneOff, Play, Square, X } from "lucide-react";
import { VOICE_OPTIONS } from "@/lib/constants/agent-onboarding";
import { backdrop } from "@/lib/glass/style";

/** Minimal surface of retell-client-js-sdk's RetellWebClient (3.x) that we use. */
interface WebClient {
    startCall: (opts: {
        accessToken: string;
        callId?: string;
        transport?: "gateway" | "livekit";
        iceServers?: RTCIceServer[];
        url?: string;
        emitRawAudioSamples?: boolean;
    }) => Promise<void>;
    stopCall: () => void;
    on: (event: string, cb: (...args: unknown[]) => void) => void;
    /** Analyser on the agent's incoming audio; set once the call's audio is ready. */
    analyzerComponent?: { calculateVolume: () => number };
}

/**
 * Retell's v3 "gateway" web calls do not relay agent_start_talking /
 * agent_stop_talking to the browser (confirmed by Retell, 2026-09-22), so the
 * "speaking" state falls back to the agent audio's own volume. A short hold
 * keeps it from flickering between words.
 */
const TALK_VOLUME_ON = 0.03;
const TALK_HOLD_MS = 350;

type CallState = "idle" | "connecting" | "active" | "ended" | "error";

const VOICE_STORAGE_KEY = "ethos_demo_voice";

/**
 * Languages each demo agent can hold a live call in. Mirrors the language
 * config on the Retell demo agents (each agent is limited to one speech
 * recognition provider, and each voice supports a different range, so the
 * sets vary per voice). Keep in sync when the agents' languages change.
 */
const MEGA_LANGS = [
    "English", "Spanish", "French", "German", "Hindi", "Russian", "Portuguese", "Japanese", "Italian",
    "Dutch", "Arabic", "Turkish", "Farsi", "Hebrew", "Vietnamese", "Korean", "Thai", "Indonesian", "Malay",
];
const DEMO_VOICE_LANGS: Readonly<Record<string, string[]>> = {
    "minimax-Kevin": ["English", "Mandarin", "Cantonese", "Japanese", "Korean"],
    "minimax-May": ["English", "Mandarin", "Cantonese", "Japanese", "Korean"],
    "11labs-Samad": ["English", "Hindi", "Urdu", "Tamil", "Kannada", "Marathi"],
    "11labs-Amritanshu": ["English", "Hindi", "Urdu", "Tamil", "Kannada", "Marathi"],
    "inworld-Maricel": ["English", "Spanish", "French", "German", "Hindi", "Russian", "Portuguese", "Japanese", "Italian", "Dutch", "Arabic", "Hebrew", "Korean"],
    "inworld-Andoy": ["English", "Spanish", "French", "German", "Hindi", "Russian", "Portuguese", "Japanese", "Italian", "Dutch", "Arabic", "Hebrew", "Korean"],
    "cartesia-Eva": MEGA_LANGS.filter((l) => l !== "Farsi"),
};
const langsOf = (key: string): string[] => DEMO_VOICE_LANGS[key] ?? MEGA_LANGS;

/**
 * Maya (the signature demo persona) + every wizard voice, demoable live.
 * The chooser groups by accent, so tone alone goes on the card; the accent is
 * always readable in the group header even when card text truncates.
 */
const DEMO_VOICES: ReadonlyArray<{ key: string; label: string; tone: string; accent: string; sampleUrl?: string }> = [
    { key: "maya", label: "Maya", tone: "Signature demo voice", accent: "Signature" },
    ...VOICE_OPTIONS.map((v) => ({ key: v.id, label: v.label, tone: v.description.split(" · ")[0], accent: v.accent, sampleUrl: v.sampleUrl })),
];

function loadStoredVoice(): string {
    try {
        const v = sessionStorage.getItem(VOICE_STORAGE_KEY);
        if (v && DEMO_VOICES.some((d) => d.key === v)) return v;
    } catch { /* storage unavailable */ }
    return "maya";
}

/**
 * "Talk to our AI live" — a browser voice call with the demo receptionist.
 * Drop-in: <LiveDemoWidget /> (optionally pass a custom trigger label/style).
 * With showVoicePicker (the sandbox / sales surfaces), clicking the trigger
 * first opens a voice chooser so reps can demo any wizard voice, not just Maya.
 */
export default function LiveDemoWidget({
    label = "Talk to our AI live",
    triggerStyle,
    showVoicePicker = false,
    maxSeconds = 115,
}: {
    label?: string;
    triggerStyle?: React.CSSProperties;
    showVoicePicker?: boolean;
    /**
     * Client-side call cap in seconds: wind-down banner at maxSeconds - 25,
     * polite auto-end at maxSeconds. Must stay just under the demo agent's
     * server-side max_call_duration_ms (hero Maya 2 min, sandbox agents 10 min).
     */
    maxSeconds?: number;
}) {
    const [state, setState] = useState<CallState>("idle");
    const [agentTalking, setAgentTalking] = useState(false);
    const [windingDown, setWindingDown] = useState(false);
    const [error, setError] = useState("");
    const [chooserOpen, setChooserOpen] = useState(false);
    const [voiceKey, setVoiceKey] = useState("maya");
    const [previewingKey, setPreviewingKey] = useState<string | null>(null);
    const clientRef = useRef<WebClient | null>(null);
    const endedByTimeRef = useRef(false);
    const timersRef = useRef<number[]>([]);
    const previewRef = useRef<HTMLAudioElement | null>(null);

    useEffect(() => {
        if (showVoicePicker) setVoiceKey(loadStoredVoice());
    }, [showVoicePicker]);

    const stopPreview = useCallback(() => {
        try {
            previewRef.current?.pause();
        } catch { /* not playing */ }
        setPreviewingKey(null);
    }, []);

    const togglePreview = useCallback((key: string, url: string) => {
        if (previewingKey === key) {
            stopPreview();
            return;
        }
        if (!previewRef.current) previewRef.current = new Audio();
        const a = previewRef.current;
        a.onended = () => setPreviewingKey(null);
        a.src = url;
        a.currentTime = 0;
        void a.play().catch(() => setPreviewingKey(null));
        setPreviewingKey(key);
    }, [previewingKey, stopPreview]);

    const meterRef = useRef<number | null>(null);

    const stopClient = useCallback(() => {
        timersRef.current.forEach((t) => window.clearTimeout(t));
        timersRef.current = [];
        if (meterRef.current !== null) { window.clearInterval(meterRef.current); meterRef.current = null; }
        setWindingDown(false);
        try {
            clientRef.current?.stopCall();
        } catch {
            /* already stopped */
        }
        clientRef.current = null;
    }, []);

    // Safety: clear any pending demo-timer + sample audio on unmount (e.g. sandbox navigation).
    useEffect(() => () => {
        timersRef.current.forEach((t) => window.clearTimeout(t));
        try { previewRef.current?.pause(); } catch { /* fine */ }
    }, []);

    const endCall = useCallback(() => {
        stopClient();
        setAgentTalking(false);
        setState("ended");
        window.setTimeout(() => setState((s) => (s === "ended" ? "idle" : s)), 2500);
    }, [stopClient]);

    const fail = useCallback((msg: string) => {
        stopClient();
        setAgentTalking(false);
        setError(msg);
        setState("error");
        window.setTimeout(() => setState((s) => (s === "error" ? "idle" : s)), 7000);
    }, [stopClient]);

    const startCall = useCallback(async (voice: string) => {
        if (state === "connecting" || state === "active") return;
        stopPreview();
        setChooserOpen(false);
        setError("");
        setState("connecting");

        // 1) Confirm we can actually capture the mic, with a precise message if not.
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            stream.getTracks().forEach((t) => t.stop());
        } catch (e) {
            const name = (e as { name?: string })?.name;
            if (name === "NotAllowedError" || name === "SecurityError") {
                fail("Microphone blocked. Click the mic (or lock) icon in your browser's address bar, allow access, then try again.");
            } else if (name === "NotFoundError" || name === "OverconstrainedError") {
                fail("No microphone detected. Connect a mic and try again.");
            } else {
                fail("Couldn't access your microphone. Check your browser's site settings and try again.");
            }
            return;
        }

        // 2) Get a web-call token and connect.
        try {
            // The picker surfaces (sandbox) always name their voice, including
            // "maya", which maps to the long-form multilingual sandbox agent.
            // Pickerless surfaces (hero) send no voice: the tight 2-min demo.
            const res = await fetch("/api/public/web-call", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(showVoicePicker ? { voice } : {}),
            });
            if (!res.ok) throw new Error("token");
            const { accessToken, callId, transport, iceServers, url } = await res.json();
            if (!accessToken) throw new Error("token");

            const mod = await import("retell-client-js-sdk");
            const client = new mod.RetellWebClient() as unknown as WebClient;
            clientRef.current = client;
            client.on("call_started", () => {
                setState("active");
                setWindingDown(false);
                endedByTimeRef.current = false;
                timersRef.current.forEach((t) => window.clearTimeout(t));
                // Cost guard: gentle wind-down cue 25s before the cap, polite
                // auto-end at the cap (just under the agent's server hard cap).
                timersRef.current = [
                    window.setTimeout(() => setWindingDown(true), Math.max(5, maxSeconds - 25) * 1000),
                    window.setTimeout(() => { endedByTimeRef.current = true; endCall(); }, maxSeconds * 1000),
                ];
            });
            client.on("call_ended", () => endCall());
            // Real talking events win whenever the transport sends them.
            let sawTalkEvent = false;
            client.on("agent_start_talking", () => { sawTalkEvent = true; setAgentTalking(true); });
            client.on("agent_stop_talking", () => { sawTalkEvent = true; setAgentTalking(false); });
            let lastLoud = 0;
            if (meterRef.current !== null) window.clearInterval(meterRef.current);
            meterRef.current = window.setInterval(() => {
                if (sawTalkEvent || !client.analyzerComponent) return;
                let v = 0;
                try { v = client.analyzerComponent.calculateVolume(); } catch { return; }
                const now = Date.now();
                if (v > TALK_VOLUME_ON) { lastLoud = now; setAgentTalking(true); }
                else if (now - lastLoud > TALK_HOLD_MS) setAgentTalking(false);
            }, 100);
            client.on("error", (...args: unknown[]) => {
                console.error("[LiveDemo] retell error", ...args);
                fail("The call couldn't connect. Your network may be blocking voice calls — try another network or browser.");
            });
            // emitRawAudioSamples makes the SDK attach the analyser the fallback reads.
            await client.startCall({ accessToken, callId, transport, iceServers, url, emitRawAudioSamples: true });
        } catch (err) {
            console.error("[LiveDemo] start failed", err);
            fail("Couldn't start the call. Please try again in a moment.");
        }
    }, [state, endCall, fail, stopPreview, showVoicePicker, maxSeconds]);

    const pickVoice = useCallback((key: string) => {
        setVoiceKey(key);
        try { sessionStorage.setItem(VOICE_STORAGE_KEY, key); } catch { /* fine */ }
    }, []);

    const live = state === "active";
    const connecting = state === "connecting";
    const showCallPanel = live || connecting || state === "ended";
    const persona = DEMO_VOICES.find((v) => v.key === voiceKey)?.label ?? "Maya";

    return (
        <>
            <button
                onClick={() => (showVoicePicker ? setChooserOpen(true) : void startCall(voiceKey))}
                disabled={connecting || live}
                style={{
                    padding: "16px 30px",
                    fontSize: "1rem",
                    fontWeight: 600,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 10,
                    background: "linear-gradient(135deg,#10b981,#0d9488)",
                    color: "#fff",
                    border: "none",
                    borderRadius: 9999,
                    cursor: connecting || live ? "default" : "pointer",
                    boxShadow: "0 8px 30px rgba(16,185,129,0.4)",
                    ...triggerStyle,
                }}
            >
                <span style={{ position: "relative", display: "inline-flex" }}>
                    <Mic size={18} />
                    <span
                        style={{
                            position: "absolute",
                            inset: -7,
                            borderRadius: 9999,
                            border: "2px solid rgba(255,255,255,0.65)",
                            animation: "edpulse 1.8s ease-out infinite",
                        }}
                    />
                </span>
                {connecting ? "Connecting…" : label}
            </button>

            {chooserOpen && !showCallPanel && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 1000,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "rgba(3,7,14,0.72)",
                        ...backdrop("blur(6px)"),
                    }}
                >
                    <div
                        style={{
                            width: "min(600px, 94vw)",
                            borderRadius: 22,
                            background: "#0b0f1c",
                            border: "1px solid rgba(255,255,255,0.1)",
                            padding: "26px 26px 22px",
                            color: "#e2e8f0",
                            position: "relative",
                            fontFamily: "var(--font-body, system-ui)",
                        }}
                    >
                        <button
                            onClick={() => { stopPreview(); setChooserOpen(false); }}
                            aria-label="Close"
                            style={{ position: "absolute", top: 16, right: 16, background: "none", border: "none", color: "#64748b", cursor: "pointer" }}
                        >
                            <X size={18} />
                        </button>
                        <p style={{ fontSize: "1.05rem", fontWeight: 700, color: "#fff", margin: 0 }}>Pick a voice, then talk to it live</p>
                        <p style={{ fontSize: "0.8rem", color: "#94a3b8", margin: "6px 0 16px" }}>
                            Every voice from the onboarding form, demoable on a real call. Tap the play icon for a quick
                            sample. Each voice speaks 5 to 19 languages live; switch languages mid-call and it follows you.
                        </p>
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
                                gap: 8,
                                maxHeight: "46vh",
                                overflowY: "auto",
                                paddingRight: 4,
                            }}
                        >
                            {DEMO_VOICES.map((v, i) => {
                                const selected = v.key === voiceKey;
                                const newGroup = i === 0 || DEMO_VOICES[i - 1].accent !== v.accent;
                                return (
                                    <Fragment key={v.key}>
                                    {newGroup && (
                                        <div
                                            style={{
                                                gridColumn: "1 / -1",
                                                padding: i === 0 ? "0 2px" : "8px 2px 0",
                                                fontSize: "0.62rem",
                                                fontWeight: 700,
                                                letterSpacing: "0.16em",
                                                textTransform: "uppercase",
                                                color: "#64748b",
                                            }}
                                        >
                                            {v.accent}
                                        </div>
                                    )}
                                    <div
                                        onClick={() => pickVoice(v.key)}
                                        role="button"
                                        style={{
                                            textAlign: "left",
                                            padding: "10px 12px",
                                            borderRadius: 12,
                                            cursor: "pointer",
                                            background: selected ? "rgba(16,185,129,0.16)" : "rgba(255,255,255,0.04)",
                                            border: selected ? "1px solid rgba(16,185,129,0.65)" : "1px solid rgba(255,255,255,0.08)",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                        }}
                                    >
                                        <span style={{ flex: 1, minWidth: 0 }}>
                                            <span style={{ display: "block", fontSize: "0.85rem", fontWeight: 700, color: "#fff" }}>{v.label}</span>
                                            <span style={{ display: "block", fontSize: "0.68rem", color: "#94a3b8", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                {v.tone}
                                            </span>
                                        </span>
                                        {v.sampleUrl && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); togglePreview(v.key, v.sampleUrl!); }}
                                                aria-label={previewingKey === v.key ? `Stop ${v.label} sample` : `Play ${v.label} sample`}
                                                style={{
                                                    width: 28,
                                                    height: 28,
                                                    flexShrink: 0,
                                                    borderRadius: 9999,
                                                    border: "1px solid rgba(255,255,255,0.18)",
                                                    background: previewingKey === v.key ? "#10b981" : "rgba(255,255,255,0.06)",
                                                    color: "#fff",
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    cursor: "pointer",
                                                }}
                                            >
                                                {previewingKey === v.key ? <Square size={11} /> : <Play size={12} />}
                                            </button>
                                        )}
                                    </div>
                                    </Fragment>
                                );
                            })}
                        </div>
                        <div style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 8,
                            marginTop: 14,
                            padding: "10px 12px",
                            borderRadius: 12,
                            background: "rgba(16,185,129,0.08)",
                            border: "1px solid rgba(16,185,129,0.25)",
                        }}>
                            <Languages size={14} style={{ color: "#10b981", flexShrink: 0, marginTop: 2 }} />
                            <span style={{ fontSize: "0.74rem", lineHeight: 1.55, color: "#cbd5e1" }}>
                                <strong style={{ color: "#fff" }}>{persona}</strong> speaks{" "}
                                {langsOf(voiceKey).join(", ")}.
                            </span>
                        </div>
                        <button
                            onClick={() => void startCall(voiceKey)}
                            style={{
                                marginTop: 12,
                                width: "100%",
                                padding: "13px 28px",
                                borderRadius: 9999,
                                background: "linear-gradient(135deg,#10b981,#0d9488)",
                                color: "#fff",
                                border: "none",
                                fontWeight: 700,
                                fontSize: "0.95rem",
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 8,
                            }}
                        >
                            <Mic size={17} /> Start live call with {persona}
                        </button>
                    </div>
                </div>
            )}

            {showCallPanel && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 1000,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "rgba(3,7,14,0.72)",
                        ...backdrop("blur(6px)"),
                    }}
                >
                    <div
                        style={{
                            width: "min(420px, 92vw)",
                            borderRadius: 22,
                            background: "#0b0f1c",
                            border: "1px solid rgba(255,255,255,0.1)",
                            padding: 30,
                            textAlign: "center",
                            color: "#e2e8f0",
                            position: "relative",
                            fontFamily: "var(--font-body, system-ui)",
                        }}
                    >
                        <button
                            onClick={endCall}
                            aria-label="Close"
                            style={{ position: "absolute", top: 16, right: 16, background: "none", border: "none", color: "#64748b", cursor: "pointer" }}
                        >
                            <X size={18} />
                        </button>
                        <div
                            style={{
                                width: 90,
                                height: 90,
                                margin: "8px auto 18px",
                                borderRadius: 9999,
                                background: "linear-gradient(135deg,#10b981,#0d9488)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                position: "relative",
                            }}
                        >
                            <Mic size={34} color="#fff" />
                            {live && (
                                <span
                                    style={{
                                        position: "absolute",
                                        inset: -8,
                                        borderRadius: 9999,
                                        border: "3px solid rgba(16,185,129,0.5)",
                                        animation: agentTalking ? "edpulse 0.9s ease-out infinite" : "edpulse 2.2s ease-out infinite",
                                    }}
                                />
                            )}
                        </div>
                        <p style={{ fontSize: "1.1rem", fontWeight: 700, color: "#fff", margin: 0 }}>{persona} &middot; AI Receptionist</p>
                        <p style={{ fontSize: "0.85rem", color: windingDown && live ? "#fbbf24" : "#94a3b8", marginTop: 6 }}>
                            {connecting
                                ? "Connecting… allow the mic if prompted"
                                : state === "ended"
                                    ? (endedByTimeRef.current ? `That's our ${Math.max(1, Math.round(maxSeconds / 60))}-minute demo — thanks for trying it!` : "Call ended. Thanks for trying it!")
                                    : windingDown
                                        ? "Wrapping up the demo…"
                                        : agentTalking
                                            ? "Speaking…"
                                            : "Listening… go ahead and talk"}
                        </p>
                        {live && (
                            <p style={{ fontSize: "0.78rem", color: "#64748b", marginTop: 14, lineHeight: 1.55 }}>
                                Try: &ldquo;What are your hours?&rdquo;, &ldquo;I&rsquo;d like to book an appointment&rdquo;, or &ldquo;Can you take a message?&rdquo;
                            </p>
                        )}
                        {(live || connecting) && (
                            <button
                                onClick={endCall}
                                style={{
                                    marginTop: 20,
                                    padding: "12px 28px",
                                    borderRadius: 9999,
                                    background: "#ef4444",
                                    color: "#fff",
                                    border: "none",
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 8,
                                }}
                            >
                                <PhoneOff size={18} /> End call
                            </button>
                        )}
                    </div>
                </div>
            )}

            {error && (
                <p
                    style={{
                        position: "fixed",
                        bottom: 20,
                        left: "50%",
                        transform: "translateX(-50%)",
                        zIndex: 1001,
                        background: "#7f1d1d",
                        color: "#fff",
                        padding: "12px 20px",
                        borderRadius: 12,
                        fontSize: "0.85rem",
                        maxWidth: "92vw",
                        lineHeight: 1.45,
                        textAlign: "center",
                    }}
                >
                    {error}
                </p>
            )}

            <style>{`@keyframes edpulse { 0% { opacity: 0.9; transform: scale(1); } 100% { opacity: 0; transform: scale(1.6); } }`}</style>
        </>
    );
}
